import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { flashToast } from '../util/toast.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import { attachItemInfoToggle } from '../components/ItemInfoPanel.js';
import type { ForgeRecipeDto, ActiveVocationDto } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// Recipe discipline + grouping
//
// A single recipe list backs four crafting pages. Each recipe is routed to a
// page (its "discipline") and a main category within that page, derived from
// the recipe's category and slug. Within every main category the recipes are
// split into expandable TIER sub-sections (Crude → Legendary).
// ──────────────────────────────────────────────────────────────────────────────

export type Discipline = 'forge' | 'apothecary' | 'cookfire' | 'brewhouse';

// Legacy fermented-drink recipes that pre-date the brew_ prefix. They are ales,
// meads, wines and cordials — they belong in the Brewhouse, not the Apothecary.
const LEGACY_BREW_RECIPE_SLUGS = new Set([
  'forge_barleywine', 'forge_hearty_mead', 'forge_vespers_wine',
  'forge_silver_cordial', 'forge_mead_of_kings',
]);

export function disciplineOf(r: ForgeRecipeDto): Discipline {
  if (r.slug.startsWith('cook_')) return 'cookfire';
  if (r.slug.startsWith('brew_') || LEGACY_BREW_RECIPE_SLUGS.has(r.slug)) return 'brewhouse';
  if (r.category === 'consumable') return 'apothecary';
  return 'forge'; // weapon + armor (incl. jewellery)
}

// Apothecary sub-buckets — by slug (mirrors the old Forge buckets). Anything
// under 'consumable' that isn't a bandage/restorative/tonic falls into elixirs.
const BANDAGE_SLUGS = new Set([
  'forge_linen_bandage', 'forge_birchbark_dressing', 'forge_silken_bandage', 'forge_morphean_compress',
  'apoth_sage_poultice', 'apoth_amber_salve', 'apoth_kingsfoil_dressing',
]);
const RESTORATIVE_SLUGS = new Set([
  'forge_physicians_kit', 'forge_dragonbone_balm', 'forge_friars_aid',
  'apoth_silphium_panacea',
]);
const TONIC_SLUGS = new Set([
  'forge_bramble_tea',
  'apoth_mint_tea', 'apoth_chamomile_tea', 'apoth_lavender_tonic',
]);

function isJewellery(r: ForgeRecipeDto): boolean {
  return r.produces.slug.startsWith('amu_') || r.slug.startsWith('forge_amu_');
}

// Crafted wares — finished goods (furnishings, luxuries, the reliquary) that
// carry no combat numbers. They forge under category 'armor' but belong in their
// own "Goods" group, not under Armour. (Same curated-set pattern as the buckets above.)
const WARE_SLUGS = new Set([
  'craft_carpenters_chest', 'craft_walnut_cabinet', 'craft_nacre_casket', 'craft_masons_hearth',
  'craft_marble_statue', 'craft_glass_lantern', 'craft_court_perfume', 'craft_silvered_oil',
  'craft_dragonblood_varnish', 'craft_eternium_relic',
]);
function isWare(r: ForgeRecipeDto): boolean {
  return WARE_SLUGS.has(r.produces.slug);
}

// ── Tier ladder ────────────────────────────────────────────────────────────────
const TIER_ORDER = ['crude', 'common', 'fine', 'masterwork', 'legendary', 'artifact', 'mythic'] as const;
type Tier = (typeof TIER_ORDER)[number];
const TIER_LABEL: Record<string, string> = {
  crude: 'Crude', common: 'Common', fine: 'Fine', masterwork: 'Masterwork',
  legendary: 'Legendary', artifact: 'Artifact', mythic: 'Mythic',
};
function tierOf(r: ForgeRecipeDto): Tier {
  const t = r.produces.tier as Tier;
  return TIER_ORDER.includes(t) ? t : 'common';
}

// ── Page configuration ──────────────────────────────────────────────────────────
interface GroupDef {
  key: string;
  label: string;
  belongs: (r: ForgeRecipeDto) => boolean;
}
interface CraftPageConfig {
  title: string;
  bannerSlug: string;
  intro: string;
  hint: string;
  /** Which recipes belong to this page. */
  filter: (r: ForgeRecipeDto) => boolean;
  /** Main categories within the page; each splits into tier sub-sections. */
  groups: GroupDef[];
  /** Ledger vocation lines to show. */
  showSmith?: boolean;
  showAlchemist?: boolean;
  showCook?: boolean;
  showBrewer?: boolean;
}

// ── Shared page renderer ─────────────────────────────────────────────────────────
async function renderCraftingPage(root: HTMLElement, cfg: CraftPageConfig): Promise<void> {
  let recipes: ForgeRecipeDto[] = [];
  let voc: ActiveVocationDto | null = null;
  let flash: FlashState | null = null;

  // Remember which group/section accordions are expanded — across in-page
  // rebuilds AND across navigation/reload (persisted per page in localStorage).
  // null = nothing saved yet, so fall back to the "open the first craftable"
  // default; an (even empty) Set means honour exactly what the player left.
  const STORE_KEY = `rexmortis.craftopen.${cfg.bannerSlug}`;
  const loadOpen = (): Set<string> | null => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) return new Set<string>(JSON.parse(raw) as string[]);
    } catch { /* storage unavailable — non-fatal */ }
    return null;
  };
  const saveOpen = (set: Set<string>): void => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify([...set])); } catch { /* non-fatal */ }
  };
  let openKeys: Set<string> | null = loadOpen();

  async function refresh(): Promise<void> {
    try {
      const [me, recipesResp, vocResp] = await Promise.all([
        api.me(),
        api.listForgeRecipes(),
        api.activeVocation().catch(() => ({ active: null })),
      ]);
      store.set({ account: me.account, character: me.character });
      recipes = recipesResp.recipes.filter(cfg.filter);
      voc = vocResp.active;
    } catch (err) {
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load the workshop.' };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = (): void => {
      // Snapshot open accordions (groups + sections) so a rebuild doesn't slam them shut.
      const prevOpen = main.querySelectorAll<HTMLDetailsElement>('details.craft-group[open], details.craft-section[open]');
      if (prevOpen.length > 0) {
        openKeys = new Set<string>();
        prevOpen.forEach((d) => { if (d.dataset.key) openKeys!.add(d.dataset.key); });
      }

      const total = recipes.length;
      const able = recipes.filter((r) => r.craftable).length;

      // Vocation snapshot lines — each craft has a Tier-2/3/4 trade ladder.
      const TIER_NAME: Record<number, string> = { 2: 'Fine', 3: 'Masterwork', 4: 'Legendary' };
      const LADDERS: Record<string, { craftNoun: string; t2: string; t3: string; t4: string }> = {
        smith:     { craftNoun: 'smith recipes',     t2: 'Forge Apprentice', t3: 'Master Smith',    t4: 'Royal Armourer' },
        alchemist: { craftNoun: 'alchemist recipes', t2: 'Herbwife',         t3: 'Midwife',         t4: 'Court Apothecaress' },
        cook:      { craftNoun: 'cooked dishes',     t2: 'Cook',             t3: 'Master Cook',     t4: 'Royal Cook' },
        brewer:    { craftNoun: 'brews',             t2: 'Brewmaster',       t3: 'Master Vintner',  t4: 'Master Cellarer' },
      };
      const TRADE_TIER: Record<string, number> = {
        forge_apprentice: 2, master_smith: 3, royal_armourer: 4,
        apothecary: 1, herbwife: 2, midwife: 3, court_apothecaress: 4, alewife: 1,
        cook: 2, master_cook: 3, royal_cook: 4,
        brewmaster: 2, vintner: 3, master_cellarer: 4,
      };
      const TRADE_FAMILIES: Record<string, string[]> = {
        forge_apprentice: ['smith'], master_smith: ['smith'], royal_armourer: ['smith'],
        apothecary: ['alchemist'], herbwife: ['alchemist'], midwife: ['alchemist'], court_apothecaress: ['alchemist'],
        alewife: ['alchemist', 'brewer'],
        cook: ['cook'], master_cook: ['cook'], royal_cook: ['cook'],
        brewmaster: ['brewer'], vintner: ['brewer'], master_cellarer: ['brewer'],
      };
      const ladderLine = (family: keyof typeof LADDERS): string => {
        const L = LADDERS[family]!;
        let curTier = 0;
        let curName = '';
        if (voc && (TRADE_FAMILIES[voc.tradeKey] ?? []).includes(family)) {
          curTier = TRADE_TIER[voc.tradeKey] ?? 0;
          curName = voc.tradeName;
        }
        if (curTier >= 4) {
          return `Current trade: ${curName} (T4) — unlocks all ${L.craftNoun}, up to Legendary tier.`;
        }
        if (curTier >= 2) {
          const nextTier = curTier + 1;
          const nextName = nextTier === 3 ? L.t3 : L.t4;
          return `Current trade: ${curName} (T${curTier}) — unlocks ${L.craftNoun} up to ${TIER_NAME[curTier]} tier. Upgrade to ${nextName} (T${nextTier}) to craft ${TIER_NAME[nextTier]}.`;
        }
        const prefix = curTier === 1
          ? `Current trade: ${curName} (T1) — only Crude & Common. `
          : `Crude & Common ${L.craftNoun} are open to all. `;
        return `${prefix}Become ${L.t2} (T2) for Fine, ${L.t3} (T3) for Masterwork, ${L.t4} (T4) for Legendary.`;
      };
      const vocRows = [
        cfg.showSmith ? `<div class="ledger-voc"><span class="voc-tag smith">SMITH</span><span class="voc-line">${escapeHtml(ladderLine('smith'))}</span></div>` : '',
        cfg.showAlchemist ? `<div class="ledger-voc"><span class="voc-tag alchemy">ALCHEMIST</span><span class="voc-line">${escapeHtml(ladderLine('alchemist'))}</span></div>` : '',
        cfg.showCook ? `<div class="ledger-voc"><span class="voc-tag cook">COOK</span><span class="voc-line">${escapeHtml(ladderLine('cook'))}</span></div>` : '',
        cfg.showBrewer ? `<div class="ledger-voc"><span class="voc-tag brew">BREWER</span><span class="voc-line">${escapeHtml(ladderLine('brewer'))}</span></div>` : '',
      ].join('');

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/${cfg.bannerSlug}.webp" alt="" onerror="this.style.display='none'" />
        <h1>${escapeHtml(cfg.title)}</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">${escapeHtml(cfg.intro)}</p>

        <div id="flash"></div>

        <div class="forge-ledger panel">
          <div class="ledger-row">
            <div class="ledger-stat">
              <div class="ledger-label">Recipes craftable</div>
              <div class="ledger-value">${able} / ${total}</div>
            </div>
          </div>
          ${vocRows ? `<div class="ledger-row vocations">${vocRows}</div>` : ''}
          <div class="ledger-hint">${cfg.hint}</div>
        </div>

        <div id="sections"></div>
      `;

      const style = document.createElement('style');
      style.textContent = STYLES;
      main.appendChild(style);

      if (flash) flashToast(flash);

      const sectionsRoot = main.querySelector<HTMLDivElement>('#sections')!;
      let openedSomething = false;

      for (const group of cfg.groups) {
        const inGroup = recipes.filter(group.belongs);
        if (inGroup.length === 0) continue;
        const groupKey = `g:${group.key}`;
        const groupAble = inGroup.filter((r) => r.craftable).length;

        const groupEl = document.createElement('details');
        groupEl.className = 'craft-group';
        groupEl.dataset.key = groupKey;
        if (openKeys === null) {
          // First render: open the first group that has anything craftable.
          if (!openedSomething && groupAble > 0) { groupEl.open = true; openedSomething = true; }
        } else if (openKeys.has(groupKey)) {
          groupEl.open = true;
        }

        const groupSummary = document.createElement('summary');
        groupSummary.innerHTML = `
          <span class="craft-group-label">${escapeHtml(group.label)}</span>
          ${groupAble > 0 ? `<span class="forge-able-pill">${groupAble} ready</span>` : `<span class="forge-locked-pill">all locked</span>`}
          <span class="forge-section-count">${inGroup.length}</span>
        `;
        groupEl.appendChild(groupSummary);

        const tierWrap = document.createElement('div');
        tierWrap.className = 'craft-tier-wrap';

        let openedTierInGroup = false;
        for (const tier of TIER_ORDER) {
          const inTier = inGroup.filter((r) => tierOf(r) === tier);
          if (inTier.length === 0) continue;
          const sectionKey = `s:${group.key}:${tier}`;
          const tierAble = inTier.filter((r) => r.craftable).length;

          const sectionEl = document.createElement('details');
          sectionEl.className = 'craft-section';
          sectionEl.dataset.key = sectionKey;
          if (openKeys === null) {
            // First render: open the first craftable tier inside an opened group.
            if (groupEl.open && !openedTierInGroup && tierAble > 0) { sectionEl.open = true; openedTierInGroup = true; }
          } else if (openKeys.has(sectionKey)) {
            sectionEl.open = true;
          }

          const sectionSummary = document.createElement('summary');
          sectionSummary.innerHTML = `
            <span class="forge-section-label">${escapeHtml(TIER_LABEL[tier] ?? tier)}</span>
            ${tierAble > 0 ? `<span class="forge-able-pill">${tierAble} ready</span>` : `<span class="forge-locked-pill">all locked</span>`}
            <span class="forge-section-count">${inTier.length}</span>
          `;
          sectionEl.appendChild(sectionSummary);

          const grid = document.createElement('div');
          grid.className = 'forge-grid';
          const sorted = [...inTier].sort((a, b) => a.crowns - b.crowns);
          grid.innerHTML = sorted.map(renderRecipe).join('');
          sectionEl.appendChild(grid);
          tierWrap.appendChild(sectionEl);
        }

        groupEl.appendChild(tierWrap);
        sectionsRoot.appendChild(groupEl);
      }

      // Wire crafted-item info popouts.
      main.querySelectorAll<HTMLElement>('[data-item-name]').forEach((el) => {
        const slug = el.dataset.itemName;
        if (slug) attachItemInfoToggle(el, slug);
      });

      // Wire craft buttons.
      main.querySelectorAll<HTMLButtonElement>('button[data-craft]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.craft!;
          const qtyInput = main.querySelector<HTMLInputElement>(`input[data-qty="${slug}"]`);
          const qty = qtyInput ? Math.max(1, Math.min(100, Number(qtyInput.value) || 1)) : 1;
          btn.disabled = true;
          try {
            const r = await api.craft(slug, qty);
            store.set({ character: r.character });
            const producedSummary = r.produced.map((p) => `${p.qty}× ${p.name}`).join(', ');
            flash = { kind: 'success', message: `${r.recipeName} ×${r.crafted} — made ${producedSummary} (−${formatCrowns(r.crownsSpent)} crowns).` };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Crafting failed.' };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Wire Max buttons — fill the qty input with the most the player can
      // craft right now (materials, crowns and vigor, capped at the 100-per-
      // batch limit the workshop enforces).
      main.querySelectorAll<HTMLButtonElement>('button[data-craft-max]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const slug = btn.dataset.craftMax!;
          const r = recipes.find((x) => x.slug === slug);
          const qtyInput = main.querySelector<HTMLInputElement>(`input[data-qty="${slug}"]`);
          if (!r || !qtyInput) return;
          const ch = store.get().character;
          let max = 100;
          for (const m of r.materials) {
            if (m.qty > 0) max = Math.min(max, Math.floor(m.have / m.qty));
          }
          if (ch && r.crowns > 0) max = Math.min(max, Math.floor(Number(ch.crowns) / r.crowns));
          if (ch && r.vigor > 0) max = Math.min(max, Math.floor(ch.vigor / r.vigor));
          qtyInput.value = String(Math.max(1, max));
        });
      });

      // Persist expand/collapse state on every toggle, so it survives both
      // in-page rebuilds and navigating away and back / reloading.
      main.querySelectorAll<HTMLDetailsElement>('details.craft-group, details.craft-section').forEach((d) => {
        d.addEventListener('toggle', () => {
          const open = new Set<string>();
          main.querySelectorAll<HTMLDetailsElement>('details.craft-group[open], details.craft-section[open]')
            .forEach((x) => { if (x.dataset.key) open.add(x.dataset.key); });
          openKeys = open;
          saveOpen(open);
        });
      });
    };

    anchored(renderMain);
  });
}

function renderRecipe(r: ForgeRecipeDto): string {
  const reqRows = [
    cgRow('Crowns', formatCrowns(r.crowns)),
    cgRow('Vigor', String(r.vigor)),
    ...r.materials.map((m) => cgRow(`${m.qty}× <span class="cg-itemlink" data-item-name="${escapeHtml(m.slug)}" title="Click to see where to get it">${escapeHtml(m.name)}</span>`, `${m.have}/${m.qty}`, m.have >= m.qty ? '' : 'warn')),
  ];
  const rewardRows = [
    cgRow('Produces', escapeHtml(r.produces.name), 'gain'),
    cgRow('Tier', escapeHtml(r.produces.tier)),
  ];
  const blocked = r.craftable ? '' : `<div class="blocked">${escapeHtml(r.blockedReason ?? 'Cannot craft.')}</div>`;
  const vocBadge = r.vocationSlug
    ? `<span class="voc-badge">${escapeHtml(r.vocationSlug)}${r.vocationLevel ? ` L${r.vocationLevel}+` : ''}</span>`
    : '';
  return `
    <div class="recipe-card ${r.craftable ? 'able' : 'blocked'}">
      <div class="recipe-head">
        <img class="recipe-thumb" src="/img/item-thumbs/${r.produces.slug}.webp" alt="" width="52" height="52" loading="lazy" onerror="this.style.display='none'" data-item-name="${r.produces.slug}" title="Click for full info" />
        <h3 data-item-name="${r.produces.slug}" title="Click for full info">${escapeHtml(r.name)}</h3>
        ${vocBadge}
      </div>
      <p class="recipe-blurb">${escapeHtml(r.blurb)}</p>
      ${cgTable([cgCol('Requires', reqRows), cgCol('Reward', rewardRows)])}
      ${blocked}
      <div class="recipe-cta">
        <input type="number" min="1" max="100" value="1" data-qty="${r.slug}" />
        <button class="btn" data-craft-max="${r.slug}" ${r.craftable ? '' : 'disabled'} title="Fill in the most you can craft right now">Max</button>
        <button class="btn" data-craft="${r.slug}" ${r.craftable ? '' : 'disabled'}>Craft</button>
      </div>
    </div>
  `;
}

// ──────────────────────────────────────────────────────────────────────────────
// The four crafting pages
// ──────────────────────────────────────────────────────────────────────────────

export function renderForge(root: HTMLElement): Promise<void> {
  return renderCraftingPage(root, {
    title: 'The Forge',
    bannerSlug: 'forge',
    intro: 'Bring the iron, the leather, the stone. Bring the patience. The hammer does the rest.',
    hint: 'Materials come from misdeeds, defeated foes, expeditions, and the Trades of the Land (Mining, Lumbering, Hunting). Smith trades unlock the metal and leather lines at their tier and below; jewellery sets gem and precious metal into wards.',
    filter: (r) => disciplineOf(r) === 'forge',
    groups: [
      { key: 'weapons', label: 'Weapons', belongs: (r) => r.category === 'weapon' },
      { key: 'jewellery', label: 'Jewellery', belongs: (r) => r.category === 'armor' && isJewellery(r) },
      { key: 'armour', label: 'Armour', belongs: (r) => r.category === 'armor' && !isJewellery(r) && !isWare(r) && !r.slug.startsWith('smelt_') && !r.slug.startsWith('refine_') },
      { key: 'goods', label: 'Goods & Wares', belongs: (r) => isWare(r) },
      { key: 'smelting', label: 'Smelting & Refining', belongs: (r) => r.slug.startsWith('smelt_') || r.slug.startsWith('refine_') },
    ],
    showSmith: true,
  });
}

export function renderApothecary(root: HTMLElement): Promise<void> {
  return renderCraftingPage(root, {
    title: 'The Apothecary',
    bannerSlug: 'apothecary',
    intro: 'Bind the wound, steady the nerve, and risk the stronger draughts. Every cure has its price.',
    hint: 'Alchemist trades unlock the apothecary lines at their tier and below. Bandages share the medical cooldown; tonics share the tonic cooldown; elixirs share the elixir cooldown — take one and the whole elixir line locks out, with an overdose risk on the strongest.',
    filter: (r) => disciplineOf(r) === 'apothecary',
    groups: [
      { key: 'bandages', label: 'Bandages — quick patching (medical cooldown)', belongs: (r) => BANDAGE_SLUGS.has(r.slug) },
      { key: 'restoratives', label: 'Restoratives — the surgeon\'s work (medical cooldown)', belongs: (r) => RESTORATIVE_SLUGS.has(r.slug) },
      { key: 'tonics', label: 'Tonics — slow, gentle boosters (tonic cooldown)', belongs: (r) => TONIC_SLUGS.has(r.slug) },
      { key: 'elixirs', label: 'Elixirs, Roots & Powders — big payoff, overdose risk (elixir cooldown)', belongs: (r) => !BANDAGE_SLUGS.has(r.slug) && !RESTORATIVE_SLUGS.has(r.slug) && !TONIC_SLUGS.has(r.slug) },
    ],
    showAlchemist: true,
  });
}

export function renderCookfire(root: HTMLElement): Promise<void> {
  return renderCraftingPage(root, {
    title: 'The Cookfire',
    bannerSlug: 'cookfire',
    intro: 'Turn the day\'s catch and the field\'s harvest into hot food. A full belly is its own kind of armour.',
    hint: 'Cooked dishes restore Life and Spirit on their own Provisions cooldown — a channel apart from tonics and medicine. Ingredients come from Fishing, Hunting, Foraging, and Salt-Harvesting.',
    filter: (r) => disciplineOf(r) === 'cookfire',
    groups: [
      { key: 'dishes', label: 'Cooked Dishes', belongs: () => true },
    ],
    showCook: true,
  });
}

export function renderBrewhouse(root: HTMLElement): Promise<void> {
  return renderCraftingPage(root, {
    title: 'The Brewhouse',
    bannerSlug: 'brewhouse',
    intro: 'Mash, ferment, and cask. Ale for the road, mead for the hall, wine for the long game.',
    hint: 'Ale, mead and wine share the tonic cooldown; the cellared vintage counts as Provisions. Barley, hops, honey and grapes come from Foraging.',
    filter: (r) => disciplineOf(r) === 'brewhouse',
    groups: [
      { key: 'brews', label: 'Ale, Mead & Wine', belongs: () => true },
    ],
    showBrewer: true,
  });
}

const STYLES = `
  /* ── Ledger ────────────────────────────────────────────────────────── */
  .forge-ledger { display: flex; flex-direction: column; gap: var(--s-3); }
  .ledger-row { display: flex; flex-wrap: wrap; gap: var(--s-3); }
  .ledger-stat {
    flex: 1 1 160px;
    background: var(--c-parchment-dark);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-2) var(--s-3);
  }
  .ledger-label {
    font-family: var(--font-display);
    font-size: 0.78rem; letter-spacing: 0.05em; text-transform: uppercase;
    color: var(--c-ink-soft);
  }
  .ledger-value {
    font-family: var(--font-numeric);
    font-size: 1.4rem; font-weight: 700; color: var(--c-ink); margin-top: 2px;
  }
  .ledger-row.vocations { flex-direction: column; gap: var(--s-2); }
  .ledger-voc { display: flex; align-items: center; gap: var(--s-2); font-size: 0.95rem; }
  .voc-tag {
    font-family: var(--font-display);
    font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 2px 8px; border-radius: 3px; flex-shrink: 0; color: #fff5e1;
  }
  .voc-tag.smith   { background: #4a3220; }
  .voc-tag.alchemy { background: #2f4029; }
  .voc-tag.cook    { background: #6b2f1f; }
  .voc-tag.brew    { background: #5a3d12; }
  .voc-line { color: var(--c-ink); font-style: italic; }
  .ledger-hint {
    font-family: var(--font-elegant); font-style: italic; color: var(--c-ink-soft);
    font-size: 0.9rem; line-height: 1.5;
    border-top: 1px dashed var(--c-rule-soft); padding-top: var(--s-2);
  }

  /* ── Main-category accordions (outer) ──────────────────────────────── */
  .craft-group {
    margin-bottom: var(--s-3);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    background: var(--c-parchment);
  }
  .craft-group > summary {
    cursor: pointer;
    padding: var(--s-3);
    font-family: var(--font-display);
    color: var(--c-oxblood);
    font-size: 1.18rem;
    letter-spacing: 0.02em;
    list-style: none; user-select: none;
    display: flex; align-items: center; gap: var(--s-2);
    border-bottom: 1px solid transparent;
  }
  .craft-group[open] > summary { border-bottom-color: var(--c-rule-soft); }
  .craft-group > summary::-webkit-details-marker { display: none; }
  .craft-group > summary::before {
    content: '\\25B8'; color: var(--c-rule); display: inline-block; width: 1ch;
  }
  .craft-group[open] > summary::before { content: '\\25BE'; }
  .craft-group > summary:hover { background: rgba(139, 111, 71, 0.10); }
  .craft-group-label { flex: 0 1 auto; }
  .craft-tier-wrap { padding: var(--s-3); display: flex; flex-direction: column; gap: var(--s-2); }

  /* ── Tier accordions (inner) ───────────────────────────────────────── */
  .craft-section {
    border: 1px solid var(--c-rule-soft);
    border-radius: var(--radius);
    background: var(--c-parchment-dark);
  }
  .craft-section > summary {
    cursor: pointer;
    padding: var(--s-2) var(--s-3);
    font-family: var(--font-display);
    color: var(--c-ink);
    font-size: 0.98rem;
    list-style: none; user-select: none;
    display: flex; align-items: center; gap: var(--s-2);
  }
  .craft-section > summary::-webkit-details-marker { display: none; }
  .craft-section > summary::before {
    content: '\\25B8'; color: var(--c-rule); display: inline-block; width: 1ch;
  }
  .craft-section[open] > summary::before { content: '\\25BE'; }
  .craft-section > summary:hover { background: rgba(139, 111, 71, 0.08); }
  .forge-section-label { flex: 0 1 auto; }
  .forge-section-count {
    margin-left: auto; font-family: var(--font-numeric); font-size: 0.85rem; color: var(--c-ink-soft);
  }
  .forge-able-pill, .forge-locked-pill {
    font-family: var(--font-display);
    font-size: 0.72rem; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 1px 7px; border-radius: 3px;
  }
  .forge-able-pill {
    background: rgba(74, 122, 60, 0.18); color: var(--c-success); border: 1px solid var(--c-success);
  }
  .forge-locked-pill { color: var(--c-ink-soft); border: 1px solid var(--c-rule-soft); }

  /* ── Recipe cards ─────────────────────────────────────────────────── */
  .forge-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--s-3);
    padding: var(--s-3) 0 0;
  }
  .recipe-card {
    background: rgba(var(--c-surface-tint-strong), 0.55);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-3);
    display: flex; flex-direction: column; gap: var(--s-2);
  }
  .recipe-card .recipe-thumb {
    width: 52px; height: 52px; flex-shrink: 0; object-fit: cover;
    border: 1px solid var(--c-rule-soft); border-radius: 4px;
    background: var(--c-parchment-dark); cursor: pointer;
  }
  .recipe-card .recipe-thumb:hover { border-color: var(--c-gold); }
  .recipe-card.blocked { opacity: 0.85; }
  .recipe-card.able { border-color: var(--c-gold); }
  .recipe-head { display: flex; align-items: center; gap: var(--s-2); }
  .recipe-head .voc-badge { margin-left: auto; }
  .recipe-head h3 { margin: 0; font-family: var(--font-display); font-size: 1.1rem; color: var(--c-ink); }
  .voc-badge {
    font-family: var(--font-display);
    font-size: 0.78rem; letter-spacing: 0.05em; color: var(--c-oxblood);
    background: rgba(107, 31, 31, 0.1); border: 1px solid var(--c-oxblood);
    border-radius: 999px; padding: 1px 8px; text-transform: uppercase;
  }
  :root[data-theme="dark"] .voc-badge {
    color: var(--c-on-oxblood); background: var(--c-oxblood-deep); border-color: var(--c-oxblood);
  }
  .recipe-blurb {
    font-family: var(--font-elegant); font-style: italic; font-size: 0.95rem; color: var(--c-ink-soft); margin: 0;
  }
  .blocked .blocked {
    font-family: var(--font-elegant); font-style: italic; font-size: 0.88rem; color: var(--c-danger);
  }
  .recipe-cta { display: flex; gap: var(--s-2); margin-top: var(--s-2); }
  .recipe-cta input {
    width: 56px; font: inherit; font-family: var(--font-numeric); padding: 4px 6px;
    /* Colours come from the global pinned cream cell in styles/pages.css. */
    border: 1px solid var(--c-rule);
    border-radius: var(--radius); text-align: right;
  }
  .recipe-cta .btn { flex: 1 1 auto; padding: 6px 14px; font-size: 0.95rem; }
`;
